async function request(path, options = {}) {
  const response = await fetch(path, {
    ...options,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error || `Request failed (${response.status}).`);
    error.status = response.status;
    throw error;
  }
  return payload;
}

export class TeamApiProvider {
  constructor() {
    this.user = null;
    this.ready = false;
  }

  async initialize() {
    try {
      const result = await request('/api/auth/session');
      this.user = result.user || null;
      this.ready = Boolean(this.user);
      return this.user;
    } catch (error) {
      if (error.status === 401) {
        this.user = null;
        this.ready = false;
        return null;
      }
      throw error;
    }
  }

  async bootstrap() {
    try {
      const result = await request('/api/bootstrap');
      this.user = result.user || null;
      this.ready = Boolean(this.user);
      return result;
    } catch (error) {
      if (error.status === 401) {
        this.user = null;
        this.ready = false;
        return { user: null, data: null, teamUsers: [], loadedCollections: [] };
      }
      throw error;
    }
  }

  async getCollections(names) {
    if (!this.ready) throw new Error('Sign in to Team WMS before loading data.');
    const query = encodeURIComponent(names.join(','));
    return request(`/api/data?collections=${query}`);
  }

  async login(employeeCode, pin) {
    const result = await request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ employeeCode, pin }),
    });
    this.user = result.user || null;
    this.ready = Boolean(this.user);
    return this.user;
  }

  async logout() {
    await request('/api/auth/logout', { method: 'POST', body: '{}' });
    this.user = null;
    this.ready = false;
  }

  async getData() {
    if (!this.ready) throw new Error('Sign in to Team WMS before loading data.');
    return request('/api/data');
  }

  async refresh() {
    return this.getData();
  }

  async save(collection, record) {
    if (!this.ready) throw new Error('Your session has expired. Sign in again.');
    return request(`/api/data/${encodeURIComponent(collection)}`, {
      method: 'PUT',
      body: JSON.stringify(record),
    });
  }

  async remove(collection, key) {
    if (!this.ready) throw new Error('Your session has expired. Sign in again.');
    await request(`/api/data/${encodeURIComponent(collection)}/${encodeURIComponent(key)}`, { method: 'DELETE' });
    const result = await request(`/api/data/${encodeURIComponent(collection)}`);
    return result[collection] || [];
  }

  async getUsers() {
    if (!this.ready) throw new Error('Sign in to Team WMS before managing users.');
    const result = await request('/api/admin/users');
    return result.users || [];
  }

  async createUser(user) {
    if (!this.ready) throw new Error('Sign in to Team WMS before managing users.');
    return request('/api/admin/users', { method: 'POST', body: JSON.stringify(user) });
  }

  async resetUserPin(employeeCode, pin) {
    if (!this.ready) throw new Error('Sign in to Team WMS before managing users.');
    return request(`/api/admin/users/${encodeURIComponent(employeeCode)}/pin`, {
      method: 'PATCH', body: JSON.stringify({ pin }),
    });
  }

  async updateUser(employeeCode, changes) {
    if (!this.ready) throw new Error('Sign in to Team WMS before managing users.');
    return request(`/api/admin/users/${encodeURIComponent(employeeCode)}`, {
      method: 'PATCH', body: JSON.stringify(changes),
    });
  }

  async saveGuideProgress(version, status) {
    if (!this.ready) throw new Error('Sign in to Team WMS before saving guide progress.');
    return request('/api/me/guide', {
      method: 'PUT', body: JSON.stringify({ version, status }),
    });
  }
}
